import { useState } from "react";
import { Camera, Sparkles, AlertTriangle } from "lucide-react";
import { SeverityDot, severityTone } from "@/components/ui/severity-dot";
import { FindingPolarityMark } from "@/components/experience/finding-polarity-mark";
import { Kicker } from "@/components/ui/kicker";
import { cn } from "@/lib/utils";
import type { Severity, UnifiedLiveFinding } from "./types";

interface LiveWalkthroughFindingsProps {
  findings: UnifiedLiveFinding[];
  activeSeverityFilter: Severity | "all" | "helps";
  onFilterChange: (filter: Severity | "all" | "helps") => void;
  onJumpToStep: (stepNumber: number) => void;
  activeStepNumber?: number;
  projectId: string;
}

export function LiveWalkthroughFindings({
  findings,
  activeSeverityFilter,
  onFilterChange,
  onJumpToStep,
  activeStepNumber,
  projectId,
}: LiveWalkthroughFindingsProps) {
  const counts = {
    all: findings.length,
    critical: findings.filter((f) => f.severity === "critical").length,
    major: findings.filter((f) => f.severity === "major").length,
    minor: findings.filter((f) => f.severity === "minor").length,
    helps: findings.filter((f) => f.type === "helps").length,
  };

  const filtered = findings.filter((f) => {
    if (activeSeverityFilter === "all") return true;
    if (activeSeverityFilter === "helps") return f.type === "helps";
    return f.severity === activeSeverityFilter;
  });

  return (
    <div className="space-y-6 px-7 py-9">
      <div className="flex items-baseline justify-between gap-3">
        <Kicker as="h2">Run findings</Kicker>
        <span className="text-sm tabular-nums text-muted-foreground">
          {findings.length} observed
        </span>
      </div>

      {/* Filter strip */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border/60 pb-3">
        <FilterButton
          label="All"
          count={counts.all}
          active={activeSeverityFilter === "all"}
          onClick={() => onFilterChange("all")}
        />
        {counts.critical > 0 && (
          <FilterButton
            label="Critical"
            count={counts.critical}
            active={activeSeverityFilter === "critical"}
            onClick={() => onFilterChange("critical")}
            leading={<SeverityDot tone="critical" />}
          />
        )}
        {counts.major > 0 && (
          <FilterButton
            label="Major"
            count={counts.major}
            active={activeSeverityFilter === "major"}
            onClick={() => onFilterChange("major")}
            leading={<SeverityDot tone="important" />}
          />
        )}
        {counts.minor > 0 && (
          <FilterButton
            label="Minor"
            count={counts.minor}
            active={activeSeverityFilter === "minor"}
            onClick={() => onFilterChange("minor")}
            leading={<SeverityDot tone="minor" />}
          />
        )}
        {counts.helps > 0 && (
          <FilterButton
            label="Strengths"
            count={counts.helps}
            active={activeSeverityFilter === "helps"}
            onClick={() => onFilterChange("helps")}
            leading={<FindingPolarityMark type="helps" />}
          />
        )}
      </div>

      {findings.length === 0 ? (
        <div className="py-12 text-center">
          <Sparkles
            className="mx-auto h-5 w-5 text-muted-foreground"
            aria-hidden="true"
          />
          <p className="mt-3 text-base text-foreground font-medium">
            No findings recorded yet
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            As the persona navigates, usability strengths and frictions will
            appear here in real time.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No findings match this filter.
        </div>
      ) : (
        <ul className="space-y-4">
          {filtered.map((finding) => (
            <FindingCard
              key={finding.id}
              finding={finding}
              onJumpToStep={onJumpToStep}
              isCurrentStep={
                finding.stepNumber != null &&
                finding.stepNumber === activeStepNumber
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterButton({
  label,
  count,
  active,
  onClick,
  leading,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  leading?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors",
        active
          ? "bg-foreground text-background font-semibold shadow-xs"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {leading}
      <span>{label}</span>
      <span className="tabular-nums font-mono">({count})</span>
    </button>
  );
}

function FindingCard({
  finding,
  onJumpToStep,
  isCurrentStep,
}: {
  finding: UnifiedLiveFinding;
  onJumpToStep: (stepNumber: number) => void;
  isCurrentStep: boolean;
}) {
  const isStrength = finding.type === "helps";

  return (
    <li
      className={cn(
        "rounded-md border p-4 transition-colors",
        isCurrentStep
          ? "border-primary bg-primary/5"
          : "border-border/60 bg-surface-tonal hover:border-border",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className="shrink-0 pt-0.5">
            {isStrength ? (
              <FindingPolarityMark type="helps" />
            ) : (
              <SeverityDot tone={severityTone(finding.severity)} />
            )}
          </span>
          <div className="space-y-1">
            <h3 className="text-base font-medium text-foreground leading-snug">
              {finding.title || finding.description}
            </h3>
            {finding.uiFact && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Seen:</span>{" "}
                {finding.uiFact}
              </p>
            )}
            {finding.consequence && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Impact:</span>{" "}
                {finding.consequence}
              </p>
            )}
            {finding.recommendation && (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Fix:</span>{" "}
                {finding.recommendation}
              </p>
            )}
            {finding.isPreliminary && (
              <p className="text-xs text-muted-foreground pt-1">
                Preliminary · {finding.severity} · final once run syncs
              </p>
            )}
          </div>
        </div>

        {finding.stepNumber != null && (
          <button
            type="button"
            onClick={() => onJumpToStep(finding.stepNumber!)}
            className={cn(
              "inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-mono transition-colors",
              isCurrentStep
                ? "bg-foreground text-background font-semibold"
                : "border border-border/60 text-muted-foreground hover:bg-background hover:text-foreground",
            )}
            title={`View screenshot for Step ${finding.stepNumber}`}
          >
            <Camera className="h-3 w-3" aria-hidden="true" />
            <span>Step {finding.stepNumber}</span>
          </button>
        )}
      </div>
    </li>
  );
}
